import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { BRAND } from "../../content/site";
import { scrollToId } from "../../lib/scroll";
import { MaskLine, EASE } from "./Motion";
import { LogoMark } from "./Logo";

const HeroPolaroid = ({ mx, my }) => {
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 70, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 70, damping: 18 });
  return (
    <motion.div
      className="absolute left-[41%] bottom-[7%] z-20 hidden lg:block"
      style={{ perspective: 900 }}
      initial={{ opacity: 0, y: 60, rotate: -8 }}
      animate={{ opacity: 1, y: 0, rotate: -5 }}
      transition={{ duration: 1.4, delay: 1.3, ease: EASE }}
    >
      <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }} className="w-40 xl:w-48 bg-white p-2.5 pb-10 shadow-[0_30px_60px_-20px_rgba(74,74,66,0.45)]" data-testid="hero-polaroid">
        <img src="/images/hero-bride.jpg" alt="Bride portrait by Glow Motion Pictures" className="aspect-[4/5] w-full object-cover object-[50%_42%]" />
        <p className="absolute bottom-2.5 inset-x-0 text-center font-script text-xl text-gold">Since 1990</p>
      </motion.div>
    </motion.div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="home" ref={ref} onMouseMove={onMove} className="relative h-[100svh] min-h-[640px] overflow-hidden bg-cream grain" data-testid="hero-section">
      <motion.div
        className="absolute inset-x-0 top-0 h-[66%] lg:inset-0 lg:left-[40%] lg:h-full"
        initial={{ clipPath: "inset(10% 10% 10% 10% round 220px 220px 24px 24px)" }}
        animate={{ clipPath: "inset(0% 0% 0% 0% round 0px 0px 0px 0px)" }}
        transition={{ duration: 1.8, ease: EASE }}
      >
        <motion.img
          src="/images/hero.jpg"
          alt="Bride and groom celebrating with raised hands at their wedding"
          style={{ y: imgY }}
          initial={{ scale: 1.3 }}
          animate={{ scale: 1.04 }}
          transition={{ duration: 2.6, ease: EASE }}
          className="h-full w-full object-cover object-[58%_20%] lg:object-[100%_25%]"
          fetchpriority="high"
          data-testid="hero-image"
        />
        <div className="hero-vignette absolute inset-0" />
      </motion.div>
      <div className="absolute inset-x-0 top-0 h-[66%] bg-gradient-to-t from-cream via-cream/40 via-[30%] to-transparent to-[60%] lg:hidden" />
      <div className="absolute inset-y-0 left-0 hidden w-[62%] bg-gradient-to-r from-cream from-[60%] to-transparent lg:block" />

      <HeroPolaroid mx={mx} my={my} />

      <motion.div style={{ y: textY }} className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-end px-6 pb-12 sm:px-10 sm:pb-20 lg:justify-center lg:px-12 lg:pb-0">
        <div className="max-w-xl">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.5 }} className="mb-5 flex items-center gap-3">
            <LogoMark className="h-8 w-8 text-gold" />
            <span className="eyebrow" data-testid="hero-tagline">{BRAND.tagline}</span>
          </motion.div>
          <h1 className="font-script text-[3.1rem] leading-[0.95] text-ink sm:text-7xl lg:text-[4.6rem] xl:text-[5.3rem] lg:whitespace-nowrap" data-testid="hero-heading">
            <MaskLine delay={0.7}>We Capture</MaskLine>
            <MaskLine delay={0.88} className="pl-6 text-gold sm:pl-14">Every Emotions</MaskLine>
          </h1>
          <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 1.4, delay: 1.2, ease: EASE }} className="gold-line my-5 w-48 origin-left lg:my-6" />
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1.3 }} className="max-w-md lg:max-w-sm font-serif text-lg italic leading-snug text-ink sm:text-xl">
            Wedding, engagement & pre-wedding photography and films from Junagadh & Ahmedabad.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1.45 }} className="mt-8 flex flex-wrap gap-3 pr-14 sm:pr-0">
            <button onClick={() => scrollToId("packages")} className="btn-gold" data-testid="hero-cta-packages">View Packages</button>
            <button onClick={() => scrollToId("contact")} className="btn-ghost" data-testid="hero-cta-book">Book Now</button>
          </motion.div>
        </div>
      </motion.div>

      <motion.button
        onClick={() => scrollToId("about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-7 left-12 z-10 hidden flex-col items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-ink lg:flex"
        data-testid="hero-scroll-indicator"
      >
        Scroll
        <ArrowDown className="h-4 w-4 animate-bounce text-gold" />
      </motion.button>
    </section>
  );
};
